import { css, CSSObject } from '@emotion/react'
import {
  FocusIndicator,
  FontWeight,
  Size,
  Spacing,
  Timing,
  Transition,
  Typography,
  ZIndex,
} from '@opallabs/gem'
import { Position } from '../../gem'
import { ButtonIconPosition, Size as ButtonSize, ButtonProps } from './types'
import type { StyleProps } from './types'

const classNamePrefix = 'lapidary-button-'
export const selectors = {
  root: `${classNamePrefix}-root`,
  buttonContents: `${classNamePrefix}contents`,
  buttonText: `${classNamePrefix}text`,
} as const
export const state = {
  collapsedEnabled: '[aria-expanded="false"]:enabled',
  expandedEnabled: '[aria-expanded="true"]:enabled',
  disabled: 'disabled',
  iconOnly: 'icon-only',
} as const

// text on text + icon buttons is automatically hidden for narrow viewports
const hideTextQuery = `@media (max-inline-size: ${Spacing.XS * 225}px)`

const disabled = css({
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
    /**
     * Safari on MacOS 12.4 has a rendering bug on buttons that have flex, border radius,
     * opacity, and a transparent border - causing the button to disappear from the page.
     * https://bugs.webkit.org/show_bug.cgi?id=238088
     * Setting the border here manually to work around the issue for now.
     */
    border: 'none',
  },
})

const size = {
  [ButtonSize.Regular]: css({
    blockSize: Spacing.XS * 8,
    paddingInline: Spacing.S * 1.25,
    [`&.${state.iconOnly}`]: {
      paddingInline: Spacing.S,
    },
  }),
  [ButtonSize.Small]: css({
    blockSize: Spacing.XS * 6,
    paddingInline: Spacing.S,
    [`&.${state.iconOnly}`]: {
      paddingInline: Spacing.XS,
    },
  }),
}

const baseButtonStyles = ({
  displayMenuIndicator,
  theme,
}: StyleProps): CSSObject => ({
  ...Typography.T500,
  borderRadius: Size.Rounding,
  borderStyle: 'solid',
  borderWidth: 1,
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  letterSpacing: 'inherit',
  justifyContent: displayMenuIndicator ? 'space-between' : 'center',
  // used for `::before`
  position: 'relative',
  whiteSpace: 'nowrap',
  ...Transition(['border-color', 'box-shadow', 'opacity']),

  // used for hover/active overlays on top of the button’s base color
  '&::before': {
    borderRadius: 'inherit',
    content: '""',
    display: 'flex',
    // `-1` compensates for `borderWidth: 1`; without it `::before` is inset
    // by 1 pixel from the button’s outer edge
    inset: -1,
    position: 'absolute',
    transitionDuration: `${Timing.Default}ms`,
    transitionProperty: ['background-color'].join(', '),
    transitionTimingFunction: 'ease-in-out',
    zIndex: 0,
  },

  // The ring is for the keyboard: a pointer already knows where it pressed,
  // and a button keeps focus after a click.
  '&:focus-visible': {
    ...FocusIndicator[theme],
  },

  '@media (prefers-contrast: more)': {
    fontWeight: FontWeight.Semibold,
  },

  '@media (prefers-reduced-motion: reduced)': {
    transition: 'none',

    '&::before': {
      transition: 'none',
    },
  },
})

const button = (props: StyleProps): CSSObject => baseButtonStyles(props)

const children = ({
  iconPosition,
}: Pick<ButtonProps, 'iconPosition'>): CSSObject => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection:
    iconPosition === ButtonIconPosition.leading ? 'row-reverse' : 'row',
})

const icon = ({
  hideButtonText,
  iconPosition,
}: {
  hideButtonText: boolean
  iconPosition: ButtonIconPosition | undefined
}): CSSObject => {
  const iconStyles = {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    zIndex: ZIndex.Element.Level1,

    [hideTextQuery]: {
      marginInline: 0,
    },
  }
  if (hideButtonText) return iconStyles

  switch (iconPosition) {
    case ButtonIconPosition.leading:
      return {
        ...iconStyles,
        marginInlineEnd: Spacing.XS,
      }
    case ButtonIconPosition.trailing:
      return {
        ...iconStyles,
        marginInlineStart: Spacing.XS,
      }
    default:
      return iconStyles
  }
}

export const text = ({
  childrenPosition,
  hasChildren,
  hasIcon,
}: {
  childrenPosition: Position
  hasChildren: boolean
  hasIcon: boolean
}): CSSObject => {
  const base: CSSObject = {
    marginInlineEnd:
      hasChildren && childrenPosition === Position.End ? Spacing.S : 0,
    marginInlineStart:
      hasChildren && childrenPosition === Position.Start ? Spacing.S : 0,
    zIndex: ZIndex.Element.Level1,
  }
  if (!hasIcon) return base

  return {
    ...base,

    [hideTextQuery]: {
      display: 'none',
    },
  }
}

export const styles = {
  button,
  children,
  disabled,
  icon,
  selectors,
  size,
  text,
}
